/**
 * <DemoNarrator> — overlay above the live app while the guided tour runs.
 *
 * Iter-41 changes:
 *   - "Locked" pacing — every beat blocks until either the required
 *     interaction OR the user explicitly clicks "Next / Got it".
 *     No auto-advance, no skip button.
 *   - Smarter tooltip placement via the script's per-beat `placement`
 *     hint. 'avoid-dropdown' for inputs that open autocompletes;
 *     'center' for ack-only teaching beats with no target; 'left' /
 *     'right' / 'above' / 'below' force exact sides when needed.
 *   - Click-shield: the dim now actually catches clicks outside the
 *     spotlight, so the user can't wander away mid-beat. Their only
 *     paths forward are completing the action or quitting with the
 *     close button (kept as a humane escape valve).
 */
import { useLayoutEffect, useState } from "react";
import { ChevronLeft, X, ArrowRight } from "lucide-react";
import { useDemo } from "./DemoProvider";
import { useStartCheckout, useEntitlement } from "@/lib/checkout";

const PAD = 10;
const TIP_WIDTH = 380;
const TIP_GAP = 18;
// Worst-case rendered height across all beats. Most prompts are 90-150px,
// but beat 9 (compounds) lists 4 words inline and renders at 265px. We
// pad to 300px so the clampTop() math never under-estimates and pushes
// the tooltip below the viewport fold.
const TIP_HEIGHT_ESTIMATE = 300;

function rectFromTestId(testId) {
  if (!testId) return null;
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return null;
  return el.getBoundingClientRect();
}

// Mobile devices regularly hide the target below the fold when the demo
// navigates to a new page — and `<ScrollToTopOnNav>` resets scroll on
// every route change, so even targets that WERE in view get pushed away.
// We force the target into the centre of the viewport before measuring
// so the spotlight always lines up. Cheap no-op if the target is already
// fully visible.
function ensureVisible(testId) {
  if (!testId) return;
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return;
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight;
  // Sticky app header is 52px on mobile, 72px on desktop. Treat the
  // top ~80px and the bottom ~80px as "off-screen for the user" so we
  // re-centre anything sitting on the edge.
  const FOLD_TOP = 80;
  const FOLD_BOTTOM = 80;
  if (r.top < FOLD_TOP || r.bottom > vh - FOLD_BOTTOM) {
    try {
      el.scrollIntoView({ behavior: 'instant', block: 'center' });
    } catch {
      // Older browsers don't accept 'instant' — fall through to 'auto'.
      el.scrollIntoView({ block: 'center' });
    }
  }
}

function computeTipPosition(rect, placement) {
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const tipW = Math.min(TIP_WIDTH, vw - 32);
  const tipH = TIP_HEIGHT_ESTIMATE;

  // Center placement when there's no target (ack-only beats).
  if (!rect || placement === "center") {
    return {
      top: Math.max(16, (vh - tipH) / 2),
      left: Math.max(16, (vw - tipW) / 2),
      width: tipW,
      arrow: "none",
    };
  }

  const horizCenter = rect.left + rect.width / 2;
  const clampLeft = (x) => Math.max(16, Math.min(vw - tipW - 16, x));
  // Final-step y-clamp for vertical placements. Big targets (e.g. a 20-card
  // compound grid) can push the tooltip below the fold even when fitsBelow()
  // passed — we always pull it back inside the viewport so the user can
  // actually read it. The arrow position is re-computed inside arrowStyle().
  const clampTop = (y) => Math.max(16, Math.min(vh - tipH - 16, y));
  const aboveSlot = () => ({
    top: clampTop(rect.top - tipH - TIP_GAP),
    left: clampLeft(horizCenter - tipW / 2),
    width: tipW,
    arrow: "below",
  });
  const belowSlot = () => ({
    top: clampTop(rect.bottom + TIP_GAP),
    left: clampLeft(horizCenter - tipW / 2),
    width: tipW,
    arrow: "above",
  });
  const rightSlot = () => ({
    top: Math.max(16, Math.min(vh - tipH - 16, rect.top)),
    left: Math.min(vw - tipW - 16, rect.right + TIP_GAP),
    width: tipW,
    arrow: "left",
  });
  const leftSlot = () => ({
    top: Math.max(16, Math.min(vh - tipH - 16, rect.top)),
    left: Math.max(16, rect.left - tipW - TIP_GAP),
    width: tipW,
    arrow: "right",
  });

  const fitsBelow = () => rect.bottom + TIP_GAP + tipH + 16 < vh;
  const fitsAbove = () => rect.top - TIP_GAP - tipH - 16 > 0;
  const fitsRight = () => rect.right + TIP_GAP + tipW + 16 < vw;
  const fitsLeft = () => rect.left - TIP_GAP - tipW - 16 > 0;

  switch (placement) {
    case "above":
      return fitsAbove() ? aboveSlot() : belowSlot();
    case "below":
      return fitsBelow() ? belowSlot() : aboveSlot();
    case "left":
      return fitsLeft() ? leftSlot() : rightSlot();
    case "right":
    case "side":
      return fitsRight() ? rightSlot() : leftSlot();
    case "avoid-dropdown":
      // Search inputs open autocompletes BELOW themselves — don't park
      // the tooltip over them. Prefer right (LTR) so input + dropdown
      // stay fully visible; left as a fallback; never below.
      if (fitsRight()) return rightSlot();
      if (fitsLeft()) return leftSlot();
      if (fitsAbove()) return aboveSlot();
      // Last resort — place far below the expected dropdown area.
      return {
        top: Math.min(vh - tipH - 16, rect.bottom + 320),
        left: clampLeft(horizCenter - tipW / 2),
        width: tipW,
        arrow: "above",
      };
    case "auto":
    default:
      return fitsBelow() ? belowSlot() : aboveSlot();
  }
}

export default function DemoNarrator() {
  const ctx = useDemo();
  const status = ctx?.status ?? "idle";
  const beat = ctx?.beat ?? null;
  const step = ctx?.step ?? 0;
  const total = ctx?.total ?? 0;
  const controls = ctx?.controls;
  const checkout = useStartCheckout();
  const { lifetime } = useEntitlement();
  const [rect, setRect] = useState(null);
  const [tip, setTip] = useState(null);

  useLayoutEffect(() => {
    if (status === "idle" || status === "finished" || !beat) {
      setRect(null);
      setTip(null);
      return undefined;
    }
    let cancelled = false;
    const update = () => {
      // Force the target into view first — on mobile the page often
      // lands with the target below the fold, which makes the spotlight
      // appear miles away from where the user is looking.
      ensureVisible(beat.target);
      const r = rectFromTestId(beat.target);
      if (cancelled) return;
      if (!r) {
        setRect(null);
        setTip(computeTipPosition(null, beat.placement || "center"));
        return;
      }
      setRect({
        top: r.top - PAD,
        left: r.left - PAD,
        width: r.width + PAD * 2,
        height: r.height + PAD * 2,
        _raw: r,
      });
      setTip(computeTipPosition(r, beat.placement || "auto"));
    };
    update();
    const interval = setInterval(update, 250);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [beat, status]);

  if (!ctx || status === "idle") return null;
  const isFinished = status === "finished";
  const isAck = beat?.await?.kind === "ack";
  const isCenter = beat?.placement === "center" || !beat?.target;
  const text = status === "affirm" ? beat?.affirm : beat?.prompt;
  // Show a "Got it / Next" button when:
  //   • the beat is an ack-only teaching beat (prompt phase)
  //   • OR the affirm message is being shown (always wait for next)
  const showNextButton =
    (status === "prompt" && isAck) ||
    (status === "affirm" && (beat?.affirm || "").trim().length > 0);
  // For affirm phases with no message, advance silently. Dispatch is
  // deferred via queueMicrotask so it lands AFTER the current render
  // commits — that avoids React's "setState during render" warning
  // (DemoProvider's demo:next listener triggers a setStep). Firing on
  // every render of this status is intentional: it makes the advance
  // robust to brief listener-registration gaps as state transitions.
  if (status === "affirm" && !text) {
    queueMicrotask(() => document.dispatchEvent(new Event("demo:next")));
  }

  return (
    <div
      data-testid="demo-narrator-root"
      className="fixed inset-0 z-[100]"
      style={{ pointerEvents: "none" }}
    >
      {/* Click-shield — blocks interaction outside the spotlight so the
          user can't wander out of the scripted flow. Rendered as four
          panels TILED AROUND the spotlight rect; the spotlight zone is
          NOT covered, so the target element receives all clicks /
          keystrokes normally. When there is no spotlight (ack-only
          beats), the whole shield is solid and only the tooltip's "Next"
          button can be clicked. */}
      {!isFinished && rect && (
        <>
          <Shield style={{ top: 0, left: 0, right: 0, height: rect.top }} />
          <Shield style={{ top: rect.top, left: 0, width: rect.left, height: rect.height }} />
          <Shield style={{ top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height }} />
          <Shield style={{ top: rect.top + rect.height, left: 0, right: 0, bottom: 0 }} />
        </>
      )}
      {!isFinished && !rect && (
        <Shield style={{ inset: 0 }} />
      )}

      {/* Soft dim over the four panels (subtle — the click-shield is
          transparent, this layer adds the visual cue). For ack-only
          beats (no spotlight) we draw a single uniform dim panel. */}
      {!isFinished && rect ? (
        <>
          <Dim style={{ top: 0, left: 0, right: 0, height: rect.top }} />
          <Dim style={{ top: rect.top, left: 0, width: rect.left, height: rect.height }} />
          <Dim style={{ top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height }} />
          <Dim style={{ top: rect.top + rect.height, left: 0, right: 0, bottom: 0 }} />
          {/* Spotlight border ring — purely cosmetic */}
          <div
            aria-hidden
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              borderRadius: 12,
              boxShadow: "0 0 0 2px rgba(199,89,73,0.55), 0 0 32px rgba(199,89,73,0.25)",
              pointerEvents: "none",
              transition: "all 220ms ease",
            }}
          />
        </>
      ) : !isFinished ? (
        <div
          aria-hidden
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(20,18,16,0.55)",
            pointerEvents: "none",
          }}
        />
      ) : null}

      {/* Minimal controls — kept humane. Prev to revisit; Close as escape. */}
      {!isFinished && (
        <div
          className="fixed top-4 right-4 flex items-center gap-2 bg-washi/95 backdrop-blur-sm border border-borderDefault rounded-full px-3 py-1.5 shadow-md"
          style={{ pointerEvents: "auto" }}
          data-testid="demo-controls"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans tabular-nums">
            {Math.min(step + 1, total)} / {total}
          </span>
          <button
            data-testid="demo-prev"
            onClick={controls.prev}
            disabled={step === 0}
            className="p-1 text-sumi hover:text-shu disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous beat"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="w-px h-3 bg-borderDefault" />
          <button
            data-testid="demo-close"
            onClick={controls.stop}
            className="p-1 text-sumi hover:text-shu"
            aria-label="Close demo"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* The teaching tooltip */}
      {!isFinished && tip && text && (
        <div
          data-testid="demo-tooltip"
          className="fixed rounded-2xl bg-washi shadow-2xl border border-shu/30 px-5 py-4 text-sumi"
          style={{
            top: tip.top,
            left: tip.left,
            width: tip.width,
            pointerEvents: "auto",
            animation: "demoTipIn 240ms ease-out",
          }}
          key={`${step}-${status}`}
        >
          {/* Arrow — only when pointing at a target */}
          {tip.arrow !== "none" && rect && (
            <span
              aria-hidden
              className="absolute w-3 h-3 bg-washi"
              style={arrowStyle(tip.arrow, rect._raw, tip)}
            />
          )}
          {status === "affirm" && (
            <p className="text-[10px] uppercase tracking-[0.3em] text-shu font-sans mb-1.5">
              {beat?.affirmTag || "Got it"}
            </p>
          )}
          {status === "prompt" && isCenter && (
            <p className="text-[10px] uppercase tracking-[0.3em] text-sumiSoft font-sans mb-1.5">
              beat {step + 1} / {total}
            </p>
          )}
          <p className="font-serif text-base leading-relaxed">{text}</p>
          {showNextButton && (
            <button
              data-testid="demo-next"
              onClick={() => {
                if (status === "prompt" && isAck) {
                  document.dispatchEvent(new Event("demo:ack"));
                } else {
                  document.dispatchEvent(new Event("demo:next"));
                }
              }}
              className="mt-4 inline-flex items-center gap-2 bg-sumi text-washi px-4 py-2 text-[11px] uppercase tracking-[0.25em] font-sans hover:bg-shu transition-colors"
            >
              {status === "affirm" ? "Next" : "Got it · continue"}
              <ArrowRight size={12} />
            </button>
          )}
        </div>
      )}

      {/* Final CTA */}
      {isFinished && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-sumi/65 backdrop-blur-sm"
          style={{ pointerEvents: "auto" }}
        >
          <div
            className="bg-washi rounded-2xl border border-shu/30 shadow-2xl px-8 py-10 max-w-md text-center"
            data-testid="demo-cta"
          >
            {lifetime ? (
              // Demo replayed from INSIDE the paid app — no sales pitch.
              <>
                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-shu mb-2">
                  end of tour
                </p>
                <h2 className="font-serif text-3xl text-sumi mb-4">That&apos;s the tour</h2>
                <p className="font-serif text-base text-sumi mb-6 leading-relaxed">
                  You&apos;ve seen the threads. Now follow them through every kanji you meet.
                </p>
                <button
                  data-testid="demo-cta-close"
                  className="bg-sumi text-washi px-6 py-3 font-sans uppercase tracking-[0.25em] text-sm hover:bg-shu transition-colors w-full rounded-full"
                  onClick={controls?.stop}
                >
                  Back to the app
                </button>
              </>
            ) : (
              <>
                <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-sumiSoft mb-2">
                  one payment · lifetime · 14-day refund
                </p>
                <h2 className="font-serif text-5xl text-sumi mb-4">£20</h2>
                <p className="font-serif text-base text-sumi mb-6 leading-relaxed">
                  Two thousand kanji, every connection — install it on any
                  device and study offline.
                </p>
                <button
                  data-testid="demo-cta-buy"
                  className="bg-shu text-washi px-6 py-3 font-sans uppercase tracking-[0.25em] text-sm hover:bg-sumi transition-colors w-full rounded-full disabled:opacity-50"
                  disabled={checkout.isPending}
                  onClick={() => checkout.mutate()}
                >
                  {checkout.isPending ? "Redirecting…" : "Unlock for £20"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes demoTipIn {
          from { opacity: 0; transform: translateY(6px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

function Shield({ style }) {
  return (
    <div
      aria-hidden
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
      style={{
        position: "fixed",
        pointerEvents: "auto",
        background: "transparent",
        ...style,
      }}
    />
  );
}

function Dim({ style }) {
  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        background: "rgba(20,18,16,0.55)",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}

function arrowStyle(arrow, targetRect, tip) {
  // Use explicit per-side borders instead of the `border:` shorthand —
  // React warns when shorthand + non-shorthand overrides for the same
  // sides coexist (which arrows do by hiding two sides per direction).
  const BORDER = "1px solid rgba(199,89,73,0.30)";
  const base = {
    background: "var(--washi, #F4F1EB)",
    borderTop: BORDER,
    borderRight: BORDER,
    borderBottom: BORDER,
    borderLeft: BORDER,
  };
  if (arrow === "above") {
    // Arrow on the TOP of the tip — tip is below the target.
    const targetCx = targetRect.left + targetRect.width / 2;
    const arrowLeft = Math.max(12, Math.min(tip.width - 24, targetCx - tip.left - 6));
    return {
      ...base,
      top: -6,
      left: arrowLeft,
      transform: "rotate(45deg)",
      borderRight: "none",
      borderBottom: "none",
    };
  }
  if (arrow === "below") {
    const targetCx = targetRect.left + targetRect.width / 2;
    const arrowLeft = Math.max(12, Math.min(tip.width - 24, targetCx - tip.left - 6));
    return {
      ...base,
      bottom: -6,
      left: arrowLeft,
      transform: "rotate(45deg)",
      borderLeft: "none",
      borderTop: "none",
    };
  }
  if (arrow === "left") {
    const targetCy = targetRect.top + targetRect.height / 2;
    const arrowTop = Math.max(12, Math.min(TIP_HEIGHT_ESTIMATE - 24, targetCy - tip.top - 6));
    return {
      ...base,
      left: -6,
      top: arrowTop,
      transform: "rotate(45deg)",
      borderRight: "none",
      borderTop: "none",
    };
  }
  // right
  const targetCy = targetRect.top + targetRect.height / 2;
  const arrowTop = Math.max(12, Math.min(TIP_HEIGHT_ESTIMATE - 24, targetCy - tip.top - 6));
  return {
    ...base,
    right: -6,
    top: arrowTop,
    transform: "rotate(45deg)",
    borderLeft: "none",
    borderBottom: "none",
  };
}
